import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DollarSign,
  FileText,
  AlertTriangle,
  CreditCard,
  RefreshCw,
  Ban,
  Undo2,
  PlayCircle,
  Loader2,
  X,
  Plus,
  Receipt,
  Search,
  Trash2,
} from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { billing, members as membersApi } from '../api/endpoints.js'

const fmtMoney = (v) =>
  v != null ? `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'

const fmtDate = (d) => {
  if (!d) return '—'
  try { return format(new Date(d), 'MMM d, yyyy') } catch { return d }
}

function StatTile({ label, value, icon: Icon, accent }) {
  // Semantic, theme-aware — the previous hard-coded green-900/red-900 washed out in light mode
  const tones = {
    ok: 'text-[rgb(var(--ok))] bg-[rgb(var(--ok))]/12',
    danger: 'text-[rgb(var(--danger))] bg-[rgb(var(--danger))]/12',
    brand: 'text-primary bg-primary/12',
    default: 'text-text-muted bg-surface-3',
  }
  return (
    <div className="stat-card flex items-start gap-4">
      <div className={`p-3 rounded-xl ${tones[accent] ?? tones.default}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-text-muted font-medium">{label}</p>
        <p className="text-2xl font-bold text-text-primary mt-0.5 tabular-nums truncate">{value ?? '—'}</p>
      </div>
    </div>
  )
}

function StatusBadge({ status }) {
  const cls =
    status === 'paid' ? 'badge-active'
    : status === 'failed' ? 'badge-suspended'
    : status === 'pending' || status === 'open' ? 'badge-alert'
    : 'badge-cancelled'
  return <span className={cls}>{status ?? 'unknown'}</span>
}

function IconAction({ icon: Icon, label, onClick, busy, tone = 'default' }) {
  const tones = {
    default: 'hover:text-text-primary hover:bg-surface-4',
    ok: 'hover:text-[rgb(var(--ok))] hover:bg-[rgb(var(--ok))]/12',
    danger: 'hover:text-[rgb(var(--danger))] hover:bg-[rgb(var(--danger))]/12',
  }
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={busy}
      onClick={(e) => { e.stopPropagation(); onClick() }}
      className={`p-1.5 rounded-md text-text-dim transition-all duration-150 ease-snap
                  disabled:opacity-40 active:scale-95 ${tones[tone]}`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
    </button>
  )
}

function RefundDialog({ payment, onClose, onSubmit, busy }) {
  const [amount, setAmount] = useState(payment?.amount ?? '')
  const [reason, setReason] = useState('')
  if (!payment) return null
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/55 backdrop-blur-sm animate-fade-in p-4"
      onClick={onClose}
    >
      <div className="card w-full max-w-md animate-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">Refund payment</h3>
            <p className="text-sm text-text-muted mt-0.5">
              Payment <span className="font-mono">{payment.id}</span>
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="label" htmlFor="refund-amount">Amount</label>
        <input
          id="refund-amount"
          type="number" step="0.01" min="0"
          className="input-field mb-3"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />

        <label className="label" htmlFor="refund-reason">Reason (optional)</label>
        <input
          id="refund-reason"
          className="input-field mb-5"
          placeholder="Duplicate charge, goodwill…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-danger"
            disabled={busy || !amount}
            onClick={() => onSubmit(parseFloat(amount), reason || null)}
          >
            {busy ? 'Refunding…' : `Refund ${fmtMoney(amount)}`}
          </button>
        </div>
      </div>
    </div>
  )
}


/** Shared member picker used by both money dialogs. */
function MemberPicker({ value, onPick }) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const { data } = useQuery({
    queryKey: ['members', 'billing-search', q],
    queryFn: () => membersApi.list({ q, page: 1, per_page: 8 }).then((r) => r.data),
    enabled: open && q.length >= 2,
  })
  const results = Array.isArray(data) ? data : data?.items ?? []
  const label = (m) => m?.full_name || m?.email || m?.id

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 input-field !py-2">
        <span className="truncate text-sm">{label(value)}</span>
        <button onClick={() => onPick(null)} className="text-text-dim hover:text-text-primary shrink-0"
                aria-label="Clear member">
          <X className="w-4 h-4" />
        </button>
      </div>
    )
  }
  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-dim pointer-events-none" />
      <input className="input-field !pl-8" placeholder="Search a member…" value={q}
             onFocus={() => setOpen(true)}
             onChange={(e) => { setQ(e.target.value); setOpen(true) }} />
      {open && q.length >= 2 && (
        <div className="absolute z-30 left-0 right-0 mt-1 card !p-1 max-h-52 overflow-y-auto">
          {results.length === 0
            ? <div className="px-3 py-2 text-sm text-text-dim">No matches</div>
            : results.map((m) => (
              <button key={m.id} type="button"
                      onClick={() => { onPick(m); setOpen(false); setQ('') }}
                      className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-surface-3 transition-colors truncate">
                {label(m)}
              </button>
            ))}
        </div>
      )}
    </div>
  )
}

function ChargeDialog({ onClose }) {
  const qc = useQueryClient()
  const [member, setMember] = useState(null)
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')

  const go = useMutation({
    mutationFn: () => billing.charge({
      member_id: member.id, amount: Number(amount), description,
    }),
    onSuccess: () => {
      toast.success('Charge submitted')
      qc.invalidateQueries({ queryKey: ['billing'] })
      onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Charge failed'),
  })

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
         onClick={onClose}>
      <div className="card w-full max-w-md animate-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold">Quick charge</h3>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <label className="label">Member</label>
        <div className="mb-3"><MemberPicker value={member} onPick={setMember} /></div>
        <label className="label">Amount</label>
        <input type="number" step="0.01" min="0" className="input-field mb-3" value={amount}
               onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
        <label className="label">Description</label>
        <input className="input-field" value={description} onChange={(e) => setDescription(e.target.value)}
               placeholder="Drop-in, merchandise, late fee…" />
        <p className="text-xs text-text-dim mt-3">
          Requires a configured Stripe key — STRIPE_SECRET_KEY is currently empty.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={go.isPending || !member || !amount}
                  onClick={() => go.mutate()}>
            {go.isPending ? 'Charging…' : `Charge ${amount ? fmtMoney(amount) : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}

function InvoiceDialog({ onClose }) {
  const qc = useQueryClient()
  const [member, setMember] = useState(null)
  const [dueDate, setDueDate] = useState('')
  const [description, setDescription] = useState('')
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: '' }])

  const setItem = (i, k, v) =>
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)))
  const total = items.reduce((sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0)

  const go = useMutation({
    mutationFn: () => billing.createInvoice({
      member_id: member.id,
      due_date: dueDate || null,
      description: description || null,
      items: items
        .filter((it) => it.description && it.unit_price !== '')
        .map((it) => ({
          description: it.description,
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price),
        })),
    }),
    onSuccess: () => {
      toast.success('Invoice created')
      qc.invalidateQueries({ queryKey: ['billing'] })
      onClose()
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not create invoice'),
  })

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
         onClick={onClose}>
      <div className="card w-full max-w-lg animate-pop max-h-[88vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold">New invoice</h3>
          <button onClick={onClose} className="btn-ghost p-1.5" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <label className="label">Member</label>
        <div className="mb-3"><MemberPicker value={member} onPick={setMember} /></div>

        <label className="label">Line items</label>
        <div className="space-y-2 mb-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className="input-field flex-1" placeholder="Description"
                     value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} />
              <input type="number" min="1" className="input-field !w-16" value={it.quantity}
                     onChange={(e) => setItem(i, 'quantity', e.target.value)} />
              <input type="number" step="0.01" min="0" className="input-field !w-24" placeholder="0.00"
                     value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} />
              {items.length > 1 && (
                <button onClick={() => setItems((a) => a.filter((_, idx) => idx !== i))}
                        className="p-1.5 rounded-md text-text-dim hover:text-[rgb(var(--danger))] transition-colors"
                        aria-label="Remove line">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        <button className="btn-ghost !px-2 text-xs inline-flex items-center gap-1 mb-3"
                onClick={() => setItems((a) => [...a, { description: '', quantity: 1, unit_price: '' }])}>
          <Plus className="w-3 h-3" /> Add line
        </button>

        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">Due date</label>
            <input type="date" className="input-field" value={dueDate}
                   onChange={(e) => setDueDate(e.target.value)} /></div>
          <div><label className="label">Note</label>
            <input className="input-field" value={description}
                   onChange={(e) => setDescription(e.target.value)} /></div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-line">
          <span className="text-sm text-text-muted">Total</span>
          <span className="text-lg font-bold tabular-nums">{fmtMoney(total)}</span>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={go.isPending || !member || total <= 0}
                  onClick={() => go.mutate()}>
            {go.isPending ? 'Creating…' : 'Create invoice'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function BillingPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('invoices')
  const [refundTarget, setRefundTarget] = useState(null)
  const [charging, setCharging] = useState(false)
  const [invoicing, setInvoicing] = useState(false)
  const [busyId, setBusyId] = useState(null)

  const { data: summary } = useQuery({
    queryKey: ['billing', 'summary'],
    queryFn: () => billing.summary().then((r) => r.data),
  })

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => billing.listInvoices({ page: 1, page_size: 25 }).then((r) => r.data),
  })

  const { data: failedData, isLoading: failedLoading } = useQuery({
    queryKey: ['billing', 'failed'],
    queryFn: () => billing.failed({ page: 1, page_size: 25 }).then((r) => r.data),
  })

  const asList = (d) => (Array.isArray(d) ? d : d?.items ?? [])
  const invoices = asList(invoicesData)
  const failed = asList(failedData)

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['billing'] })
  }

  const act = useMutation({
    mutationFn: ({ kind, id, args }) => {
      if (kind === 'collect') return billing.collectInvoice(id)
      if (kind === 'retry') return billing.retryInvoice(id)
      if (kind === 'void') return billing.voidInvoice(id)
      if (kind === 'refund') return billing.refundPayment(id, args.amount, args.reason)
      throw new Error(`unknown action ${kind}`)
    },
    onMutate: ({ id }) => setBusyId(id),
    onSuccess: (_res, { kind }) => {
      toast.success(
        kind === 'collect' ? 'Collection attempted'
        : kind === 'retry' ? 'Retry queued'
        : kind === 'void' ? 'Invoice voided'
        : 'Refund issued',
      )
      setRefundTarget(null)
      refresh()
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Action failed')
    },
    onSettled: () => setBusyId(null),
  })

  const nightly = useMutation({
    mutationFn: () => billing.nightlyRetry(),
    onSuccess: (r) => {
      const n = r?.data?.retried ?? r?.data?.count
      toast.success(n != null ? `Retry sweep run — ${n} processed` : 'Retry sweep run')
      refresh()
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Retry sweep failed'),
  })

  const rows = tab === 'invoices' ? invoices : failed
  const loading = tab === 'invoices' ? invoicesLoading : failedLoading

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Billing</h1>
        <div className="flex items-center gap-2 flex-wrap">
        <button className="btn-secondary inline-flex items-center gap-2" onClick={() => setInvoicing(true)}>
          <Receipt className="w-4 h-4" /> New invoice
        </button>
        <button className="btn-primary inline-flex items-center gap-2" onClick={() => setCharging(true)}>
          <Plus className="w-4 h-4" /> Quick charge
        </button>
        <button
          className="btn-secondary inline-flex items-center gap-2"
          disabled={nightly.isPending}
          onClick={() => nightly.mutate()}
          title="Attempt collection on every failed invoice"
        >
          {nightly.isPending
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <RefreshCw className="w-4 h-4" />}
          Run retry sweep
        </button>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatTile label="MRR" value={fmtMoney(summary?.mrr)} icon={DollarSign} accent="ok" />
        <StatTile label="Outstanding" value={fmtMoney(summary?.outstanding)} icon={FileText} accent="brand" />
        <StatTile label="Failed Payments" value={summary?.failed_count?.toLocaleString() ?? failed.length} icon={AlertTriangle} accent="danger" />
        <StatTile label="Collected This Month" value={fmtMoney(summary?.collected_this_month)} icon={CreditCard} accent="default" />
      </div>

      <div className="flex items-center gap-2">
        <button
          className={`tab-btn ${tab === 'invoices' ? 'tab-btn-active' : 'tab-btn-inactive'}`}
          onClick={() => setTab('invoices')}
        >
          Invoices
        </button>
        <button
          className={`tab-btn inline-flex items-center gap-2 ${tab === 'failed' ? 'tab-btn-active' : 'tab-btn-inactive'}`}
          onClick={() => setTab('failed')}
        >
          Failed payments
          {failed.length > 0 && (
            <span className="badge-suspended !px-1.5 !py-0 text-[10px]">{failed.length}</span>
          )}
        </button>
      </div>

      <div className="table-container">
        <table className="table-base">
          <thead className="table-head">
            <tr>
              <th>Invoice</th>
              <th>Member</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
              <th>Due</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-t border-surface-3">
                  {Array.from({ length: 7 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="skeleton h-4 w-3/4" /></td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-text-dim">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  {tab === 'failed' ? 'No failed payments. ' : 'No invoices found.'}
                </td>
              </tr>
            ) : (
              rows.map((inv, i) => {
                const id = inv.id ?? inv.invoice_id
                const busy = busyId === id
                const paid = inv.status === 'paid'
                const voided = inv.status === 'void' || inv.status === 'voided'
                return (
                  <tr key={id ?? i} className="border-t border-surface-3 hover:bg-surface-3/70 transition-colors">
                    <td className="px-4 py-3 text-sm font-mono text-text-muted">
                      #{id ?? inv.invoice_number ?? i + 1}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-primary">{inv.member_name ?? inv.member_id ?? '—'}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-text-primary tabular-nums">{fmtMoney(inv.amount)}</td>
                    <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                    <td className="px-4 py-3 text-sm text-text-muted">{fmtDate(inv.created_at ?? inv.invoice_date)}</td>
                    <td className="px-4 py-3 text-sm text-text-muted">{fmtDate(inv.due_date)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-0.5">
                        {!paid && !voided && (
                          <IconAction
                            icon={PlayCircle} label="Collect now" tone="ok" busy={busy}
                            onClick={() => act.mutate({ kind: 'collect', id })}
                          />
                        )}
                        {!paid && !voided && (
                          <IconAction
                            icon={RefreshCw} label="Retry payment" busy={busy}
                            onClick={() => act.mutate({ kind: 'retry', id })}
                          />
                        )}
                        {paid && (
                          <IconAction
                            icon={Undo2} label="Refund" tone="danger" busy={busy}
                            onClick={() => setRefundTarget({ id: inv.payment_id ?? id, amount: inv.amount })}
                          />
                        )}
                        {!paid && !voided && (
                          <IconAction
                            icon={Ban} label="Void invoice" tone="danger" busy={busy}
                            onClick={() => {
                              if (window.confirm('Void this invoice? This cannot be undone.')) {
                                act.mutate({ kind: 'void', id })
                              }
                            }}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {charging && <ChargeDialog onClose={() => setCharging(false)} />}
      {invoicing && <InvoiceDialog onClose={() => setInvoicing(false)} />}

      <RefundDialog
        payment={refundTarget}
        busy={act.isPending}
        onClose={() => setRefundTarget(null)}
        onSubmit={(amount, reason) =>
          act.mutate({ kind: 'refund', id: refundTarget.id, args: { amount, reason } })
        }
      />
    </div>
  )
}
